<template>
  <div id="app" :class="{ 'tour-padding': isViewingTour }">
    <router-view/>
    <TheLoader v-if="isLoading" />
  </div>
</template>
<script>

import store from '@/store'
import { mapGetters, mapActions } from 'vuex'
import { EventBus } from '@/utils/eventBus'
import { Translation } from '@/utils/Translation'
import { AUTH_COOKIE_NAME, THIRTY_DAYS_IN_SECONDS, HIDE_CUBA_RECHARGE_BANNER } from '@/utils/constants'
import TheLoader from '@/components/TheLoader'
import { isNull, objectHasKeys } from '@/utils/comparators'
import { getGA4ClientId, getGA4SessionId } from '@/utils/tracking'
export default {
  name: 'App',
  components: {
    TheLoader
  },
  created () {
    this.enableLoading()
    this.whenUserIsUnauthorized()
    this.whenUserClicksLogout()
    this.whenTourIsVisibleAddSomePadding()
    this.whenUserBalanceChanges()
  },
  mounted () {
    this.acknowledgementDefaults()
  },
  methods: {
    ...mapActions({
      enableLoading: 'app/enableLoading'
    }),
    acknowledgementDefaults () {
      this.$toastr.defaultPosition = 'toast-bottom-center'
      this.$toastr.defaultTimeout = 5000
      this.$toastr.defaultProgressBar = false
      this.$toastr.defaultPreventDuplicates = true
      this.$toastr.defaultClassNames = ['tw-container']
      this.$toastr.defaultStyle = {
        width: '100%'
      }
    },
    whenTourIsVisibleAddSomePadding () {
      let self = this
      EventBus.onTourStartEvent(function () {
        self.isViewingTour = true
      })
      EventBus.onTourEndEvent(function () {
        self.isViewingTour = false
      })
    },
    /**
    * Event listener for unauthorized event
    */
    whenUserIsUnauthorized () {
      EventBus.onUnauthorizedEvent(this.logout)
    },
    /**
    * Event listener for logout event
    */
    whenUserClicksLogout () {
      EventBus.onLogoutEvent(this.logout)
    },
    /**
    * Unsubscribe event handlers, remove auth cookie and redirect user to login view.
    */
    logout (event) {
      let returnUrl = (!isNull(event) && objectHasKeys(event))
        ? event.returnUrl
        : null
      let localeFromParam = (!isNull(event) && objectHasKeys(event))
        ? event.locale
        : null
      this.removeAuthCookie()
      this.redirectToLoginView(returnUrl, localeFromParam)
      store.dispatch('user/locally/setPropToUser', {key: HIDE_CUBA_RECHARGE_BANNER, value: false})
      store.dispatch('reset')
    },
    /**
    * Removes the authentication cookie if exists.
    */
    removeAuthCookie () {
      this.$cookies.remove(AUTH_COOKIE_NAME, '/', process.env.COOKIE_DOMAIN)
    },
    /**
    * Redirect to login view with current user locale.
    */
    redirectToLoginView (returnUrl, localeFromParam) {
      let locale = localeFromParam || Translation.getUserSupportedLang()
      const route = {
        name: 'Signin',
        params: {
          locale: locale
        }
      }
      if (!isNull(returnUrl)) {
        route.query = {
          returnurl: returnUrl
        }
      }
      this.$router.push(route)
      // let logoutUrl = process.env.MY_URL + resource
      // window.location.href = logoutUrl
    },
    /**
     * Triggers log out tag from GTM on mParticle
     */
    /**
    * When user makes a purchase or redeems a voucher we need to update
    * user balance and products in our store to reflect the changes in ui.
    */
    whenUserBalanceChanges () {
      EventBus.onBalanceChangedEvent(function () {
        store.dispatch('user/fetchUser')
        store.dispatch('user/products/fetchUserProducts')
      })
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'app/isLoading'
    }),
    mparticle () {
      let cookieVal = this.$cookies.get('mParticleDeviceId')
      let utmParams = ['utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic', 'gclid', 'msclkid', 'awaid', 'awgid', 'awbid', 'awpid', 'awcr', 'awc']
      let userAttributes = {}
      utmParams.forEach((key) => {
        if (this.$cookies.get(key)) {
          userAttributes[key] = this.$cookies.get(key)
        }
      })
      if (!process.env.LOAD_MPARTICLE) {
        return ''
      }
      window.mParticle = {
        config: {
          deviceId: cookieVal,
          isDevelopmentMode: process.env.MP_DEV_MODE,
          v1SecureServiceUrl: 'mp.rebtel.com/webevents/v1/JS/',
          v2SecureServiceUrl: 'mp.rebtel.com/webevents/v2/JS/',
          v3SecureServiceUrl: 'mp.rebtel.com/webevents/v3/JS/',
          configUrl: 'mp.rebtel.com/tags/JS/v2/',
          identityUrl: 'mp.rebtel.com/identity/v1/',
          aliasUrl: 'mp.rebtel.com/webevents/v1/identity/',
          dataPlan: {
            planId: 'client_web'
          },
          identityCallback: (result) => {
            // You can check if there's a populated user object, otherwise there was an error
            // You can also inspect the result.httpCode - see below for a description of the supported codes
            if (result.getUser()) {
              // linter doesnt like promise in computed and vue-meta doesnt allow passing methods to innerHtml - this is the compromise
              /* eslint-disable */
              if (typeof window.gtag === 'function') {
                Promise.all([getGA4SessionId(), getGA4ClientId()]).then((response) => {
                  result.getUser().setUserAttributes({ga_session_id: response[0], client_id: response[1]})
                })
              }
              /* eslint-enable */
              this.$cookies.set('mParticleDeviceId', window.mParticle.getDeviceId(), THIRTY_DAYS_IN_SECONDS, '/', process.env.COOKIE_DOMAIN)
              result.getUser().setUserAttributes(userAttributes)
            } else {
              // the IDSync call failed - see below for more details on failed requests
            }
          }
        }
      }

      let scriptBuilder = '(function (apiKey) {'
      scriptBuilder += 'window.mParticle = window.mParticle || {};'
      scriptBuilder += 'window.mParticle.eCommerce = { Cart: {} };'
      scriptBuilder += 'window.mParticle.Identity = {};'
      scriptBuilder += 'window.mParticle.config = window.mParticle.config || {'
      scriptBuilder += 'isDevelopmentMode: ' + process.env.MP_DEV_MODE
      scriptBuilder += '};'
      scriptBuilder += 'window.mParticle.config.rq = [];'
      scriptBuilder += 'window.mParticle.ready = function (f) {'
      scriptBuilder += 'window.mParticle.config.rq.push(f);'
      scriptBuilder += '};'
      scriptBuilder += 'function a(o,t){return function(){t&&(o=t+\".\"+o);var e=Array.prototype.slice.call(arguments);e.unshift(o),window.mParticle.config.rq.push(e)}}var x=[\"endSession\",\"logError\",\"logEvent\",\"logForm\",\"logLink\",\"logPageView\",\"setSessionAttribute\",\"setAppName\",\"setAppVersion\",\"setOptOut\",\"setPosition\",\"startNewSession\",\"startTrackingLocation\",\"stopTrackingLocation\"],y=[\"setCurrencyCode\",\"logCheckout\"],z=[\"login\",\"logout\",\"modify\"];x.forEach(function(o){window.mParticle[o]=a(o)}),y.forEach(function(o){window.mParticle.eCommerce[o]=a(o,\"eCommerce\")}),z.forEach(function(o){window.mParticle.Identity[o]=a(o,\"Identity\")});'
      scriptBuilder += 'var mp = document.createElement(\'script\');'
      scriptBuilder += 'mp.type = \'text/javascript\';'
      scriptBuilder += 'mp.async = true;'
      scriptBuilder += 'mp.src = (\'https:\' == document.location.protocol ? \'https://mp\' : \'http://mp\') + \'.rebtel.com/tags/JS/v2/\' + \'22d7db4c566db641a55910ff6821285b\' + \'/mparticle.js\';'
      scriptBuilder += 'var s = document.getElementsByTagName(\'script\')[0];'
      scriptBuilder += 's.parentNode.insertBefore(mp, s);'
      scriptBuilder += '})(\'22d7db4c566db641a55910ff6821285b\');'

      window.mParticle.config.onCreateBatch = (batch) => {
        // the following removes Application State Transition (AST) events, except for those uploaded on first-run
        // First-run AST events are used by many server-side integrations to mark an "install" and are used by
        // mParticle to ensure there is a user profile created
        batch.events = batch.events.filter((event) => {
          return (event.event_type !== 'application_state_transition' || event.is_first_run)
        })
        return batch // Remember to return the batch!
      }
      return scriptBuilder
    },
    gtm () {
      let scriptBuilder = 'dataLayer = [];\n'
      scriptBuilder += '(function (w, d, s, l, i) {\n'
      scriptBuilder += '\tw[l] = w[l] || []; w[l].push({\n'
      scriptBuilder += '\t\t\t"gtm.start":\n'
      scriptBuilder += '\t\tnew Date().getTime(), event: "gtm.js"\n'
      scriptBuilder += '\t}); var f = d.getElementsByTagName(s)[0],\n'
      scriptBuilder += '\tj = d.createElement(s), dl = l != "dataLayer" ? "&l=" + l : ""; j.async = true; j.src =\n'
      scriptBuilder += '\t"//www.googletagmanager.com/gtm.js?id=" + i + dl + "' + process.env.GOOGLE_TAGMANAGER_QS + '"; f.parentNode.insertBefore(j, f);\n'
      scriptBuilder += '})(window, document, "script", "dataLayer", "' + process.env.GOOGLE_TAGMANAGER_KEY + '");'
      return scriptBuilder
    },
    gtmNoScript () {
      let scriptBuilder = '<iframe src="//www.googletagmanager.com/ns.html?id=' + process.env.GOOGLE_TAGMANAGER_KEY + process.env.GOOGLE_TAGMANAGER_QS + '"'
      scriptBuilder += ' height="0" width="0" style="display:none;visibility:hidden"></iframe>'
      return scriptBuilder
    }
  },
  data () {
    return {
      isViewingTour: false
    }
  },
  metaInfo () {
    return {
      titleTemplate: (titleChunk) => {
        return titleChunk ? `${titleChunk} | Rebtel` : this.$t('greetings.welcome')
      },
      // title: this.$t('label.loading'),
      meta: [
        { 'charset': 'utf-8' },
        { 'name': 'viewport', 'content': 'width=device-width,initial-scale=1.0' },
        { 'http-equiv': 'X-UA-Compatible', 'content': 'IE=edge' }
      ],
      script: [
        { innerHTML: this.gtm },
        { innerHTML: this.mparticle },
        { src: process.env.ADYEN_CSE_URL, defer: true },
        { src: 'https://cdn.seondf.com/js/v5/agent.js', defer: true },
        { src: 'https://cdn.optimizely.com/js/28909170182.js' }
        // { src: 'https://www.google.com/recaptcha/api.js', 'async': true, 'defer': true }
      ],
      noscript: [
        { innerHTML: this.gtmNoScript }
      ]
    }
  }
}
</script>

<style>
@import "tailwindcss/base";
@import "tailwindcss/components";
@import "tailwindcss/utilities";
</style>

<style lang="scss">
@import './assets/styles/index.scss';
// @import './assets/styles/tailwind.css';
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  &.tour-padding {
    padding-bottom: 18.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue